import * as React from "react"
import { Modal, Row, Button, Form, Container, Table } from "react-bootstrap"
import 'react-bootstrap-typeahead/css/Typeahead.css'
import { toast } from "react-toastify"
import { Subscription } from "rxjs"
import { Pool } from "../../../api/Randoms/RandomsModel/PoolData"
import FormatError from "../../../formatters/FormatError"
import { APIError } from "../../../api/ObservableFromFetch"
import { RunCancelPoolsMessages, RunCancelPoolsObservable } from "../../../api/Randoms/RandomSelectionRun"
import Checkbox from "../../Checkbox"
import RunCancelNotificationModal from "./RunCancelNotificationModal"

interface RunCancelModalProps {
  poolsData: Pool[]
  cancel: boolean
  isConsortium: boolean
  onClose: () => void
  onUpdate: () => void
}

interface RunCancelModalState {
  saving: boolean
  showConfirmation: boolean
  selectedPools: Pool[]
  selectAll: boolean
  messages: RunCancelPoolsMessages[]
}

export default class RunCancelModal extends React.Component<RunCancelModalProps, RunCancelModalState> {
  subscription?: Subscription

  state: RunCancelModalState = {
    saving: false,
    showConfirmation: false,
    selectedPools: [],
    selectAll: true,
    messages: []
  }

  saveSubscription?: Subscription

  componentWillUnmount() {
    this.saveSubscription && this.saveSubscription.unsubscribe()
  }

  handleAPIError = (error: Error | APIError, stateChanges: object) => {
    this.setState(stateChanges)
    error && toast.error(FormatError(error))
  }


  handleClose = () => {
    this.props.onClose()
  }

  handleCheckPool = (pool: Pool) => {
    let selectedPools = [...this.state.selectedPools]

    const index = selectedPools.findIndex(item => item.PoolID === pool.PoolID)
    if (index > -1) {
      selectedPools = [...selectedPools.slice(0, index), ...selectedPools.slice(index + 1)]
    } else {
      selectedPools.push(pool)
    }
    this.setState({ selectedPools: selectedPools })
  }

  executeRunCancel = () => {
    this.setState({ saving: true })
    const { selectAll, selectedPools } = this.state
    let pools = selectAll ? this.props.poolsData : selectedPools
    const poolsData = pools.map(p => { return { PoolID: p.PoolID, PoolName: p.PoolName, PoolCode: p.Code, IsConsortium: p.IsConsortium } }
    )

    this.saveSubscription && this.saveSubscription.unsubscribe()
    this.saveSubscription = RunCancelPoolsObservable(this.props.cancel, poolsData).subscribe(
      data => {
        this.setState({ saving: false, messages: data })
        toast.success("Action executed successfully.")
      },
      error => {
        this.handleAPIError(error, { saving: false })
        this.closeConfirmation()
      }
    )
  }

  handleConfirm = () => this.setState({ showConfirmation: true })

  closeConfirmation = () => {
    this.setState({ showConfirmation: false })
    if (this.state.messages.length > 0) this.props.onUpdate()
  }

  render() {
    const { cancel, poolsData, isConsortium } = this.props
    const { selectedPools, selectAll, saving, messages, showConfirmation } = this.state

    const poolRow = (pool: Pool) => {
      return <tr key={`pool_${pool.PoolID}`}>
        <td><Checkbox checked={selectedPools.includes(pool)} onChange={() => this.handleCheckPool(pool)} id={`checkbox_${pool.PoolID}`} /></td>
        <td>{pool.PoolName}</td>
        <td>{pool.Code}</td>
        {!cancel && <>
          <td>{pool.SelectionInterval}</td>
          <td>{pool.DrugSampleSize}{pool.DrugRateOrFixed === "Rate" && "%"}</td>
          <td>{pool.AlcoholSampleSize}{pool.AlcoholRateOrFixed === "Rate" && "%"}</td>
        </>}
      </tr>
    }

    return (
      <>
        {showConfirmation && <RunCancelNotificationModal
          confirmationText={`Are you sure you want to ${ cancel ? "Cancel" : "Run" } ${ isConsortium ? "Consortiums" : "Pools" }?`}
          notes={messages}
          loading={saving}
          isConsortium={this.props.isConsortium}
          onHide={this.closeConfirmation}
          onSubmit={() => this.executeRunCancel()}/>}
        <Modal backdrop="static" size="xl" centered show onHide={this.handleClose}>
          <Modal.Header closeButton>
            <Modal.Title>{`Select ${ isConsortium ? "Consortiums" : "Pools" } to ${ cancel ? "Cancel" : "Run" }`}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row className="mb-2 text-center">
                <Form.Check inline id="pools-all" name="pools" type="radio" label={`All ${ isConsortium ? "Consortiums" : "Pools" }`} className="abc-radio mx-2 ml-auto" checked={selectAll} onChange={() => this.setState({ selectAll: true })} />
                <Form.Check inline id="pools-selected" name="pools" type="radio" label={`Selected ${ isConsortium ? "Consortiums" : "Pools" }`} className="abc-radio mx-2 mr-auto" checked={!selectAll} onChange={() => this.setState({ selectAll: false })} />
              </Row>
            </Form>
            {!selectAll && <Container fluid style={{ height: "400px", overflowY: "scroll", overflowX: "hidden" }}>
              <Form.Group as={Row}>
                <Table responsive bordered className="dark text-center">
                  <thead>
                    <tr>
                      <th></th>
                      <th>{`${ isConsortium ? "Consortium" : "Pool" } Name`}</th>
                      <th>{`${ isConsortium ? "Consortium" : "Pool" } Code`}</th>
                      {!cancel && <><th>Selection Interval</th><th>Drug</th><th>Alcohol</th></>}
                    </tr>
                  </thead>
                  <tbody>{poolsData.map((pool) => poolRow(pool))}</tbody>
                </Table>
              </Form.Group>
              </Container>}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" disabled={!selectAll && selectedPools.length == 0} onClick={() => this.handleConfirm()}>Execute</Button>
            <Button variant="secondary" onClick={() => this.handleClose()}>Cancel</Button>
          </Modal.Footer>
        </Modal >
      </>
    )
  }
}
